/**
 * Toast system (shadcn Sonner, RM-117). Thin wrapper that preserves the original
 * `useToast().showToast({...})` API + `ToastProvider`/`ToastViewport` mounts, so
 * call sites and main.tsx are unchanged, while rendering through shadcn Sonner.
 * Logs are passed as plain strings (C8 — never dangerouslySetInnerHTML).
 */
import { type ReactNode } from 'react'
import { toast as sonnerToast } from 'sonner'
import { Toaster } from './ui/sonner'

export type ToastVariant = 'error' | 'info'

export interface ShowToastInput {
  title: string
  description?: string
  variant: ToastVariant
  timeoutMs?: number
}

export function useToast() {
  const showToast = ({
    title,
    description,
    variant,
    timeoutMs,
  }: ShowToastInput) => {
    // Error toasts get a longer dwell time than info (parity with the old timer).
    const duration = timeoutMs ?? (variant === 'error' ? 10_000 : 5_000)
    const emit = variant === 'error' ? sonnerToast.error : sonnerToast.info
    emit(title, { description, duration })
  }
  return { showToast }
}

/** Back-compat passthrough — Sonner needs no context provider. */
export function ToastProvider({ children }: { children: ReactNode }) {
  return <>{children}</>
}

/** Global toast viewport — render once near the router root. */
export function ToastViewport() {
  return <Toaster position="top-right" />
}
